// Mirror of backend/vtlite/units.py. Units: logMAR, Weber contrast, log CS, mm, device px.

export const ARCMIN_PER_DEG = 60;
export const LETTER_STROKES = 5;

export const logmarToLetterArcmin = (logmar: number): number => LETTER_STROKES * 10 ** logmar;
export const letterArcminToLogmar = (arcmin: number): number => Math.log10(arcmin / LETTER_STROKES);
export const logmarToSnellenDenominator = (logmar: number, numerator = 20): number =>
  numerator * 10 ** logmar;

export const weberToLogCs = (weber: number): number => {
  if (weber <= 0) throw new Error("Weber contrast must be > 0");
  return -Math.log10(weber);
};
export const logCsToWeber = (logCs: number): number => 10 ** -logCs;

export interface SnappedLetter {
  requestedLogmar: number;
  presentedLogmar: number;
  strokePx: number;
  letterPx: number;
}

export class Geometry {
  constructor(
    readonly distanceMm: number,
    readonly screenWidthMm: number,
    readonly screenWidthPx: number,
    readonly screenHeightPx: number,
  ) {}

  get pxPerMm(): number {
    return this.screenWidthPx / this.screenWidthMm;
  }

  /** Device pixels per degree at the screen centre. */
  get pxPerDeg(): number {
    const mmPerDeg = 2 * this.distanceMm * Math.tan((0.5 * Math.PI) / 180);
    return mmPerDeg * this.pxPerMm;
  }

  letterPxExact(logmar: number): number {
    return (logmarToLetterArcmin(logmar) / ARCMIN_PER_DEG) * this.pxPerDeg;
  }

  /** Round the stroke to whole device pixels (minimum 1); report what is drawn. */
  snap(logmar: number): SnappedLetter {
    const exact = this.letterPxExact(logmar);
    const strokePx = Math.max(1, roundHalfEven(exact / LETTER_STROKES));
    const letterPx = strokePx * LETTER_STROKES;
    const presentedLogmar = letterArcminToLogmar((letterPx / this.pxPerDeg) * ARCMIN_PER_DEG);
    return { requestedLogmar: logmar, presentedLogmar, strokePx, letterPx };
  }

  maxLogmar(fill = 0.8): number {
    const side = Math.min(this.screenWidthPx, this.screenHeightPx) * fill;
    return letterArcminToLogmar((side / this.pxPerDeg) * ARCMIN_PER_DEG);
  }

  minResolvableLogmar(minStrokePx = 3): number {
    const letterPx = minStrokePx * LETTER_STROKES;
    return letterArcminToLogmar((letterPx / this.pxPerDeg) * ARCMIN_PER_DEG);
  }
}

/** Python's round(): halves go to the even neighbour. */
export function roundHalfEven(x: number): number {
  const f = Math.floor(x);
  const d = x - f;
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}
